/**
 * TanStack Query staleTime configuration
 *
 * StaleTime determines how long fetched data is considered "fresh".
 * After this time, data is marked as "stale" and will be refetched in the background.
 *
 * Best practice: Differentiate based on data volatility
 * - Frequently changing data: Short staleTime (0-30s)
 * - Occasionally changing data: Medium staleTime (5-15min)
 * - Rarely changing data: Long staleTime (30min-1hr)
 * - Static/reference data: Infinity (never considered stale)
 */

export const STALE_TIME = {
  /**
   * REALTIME (0ms) - Always fresh, refetch on every access
   * Use for: Live data, notifications, real-time updates
   * Examples: Active sessions, live notifications
   */
  REALTIME: 0,

  /**
   * FREQUENT (30 seconds) - Refetch frequently
   * Use for: Data that changes often during active use
   * Examples: Inbox tasks, pending approvals, activity feeds
   */
  FREQUENT: 30 * 1000,

  /**
   * NORMAL (5 minutes) - Standard caching for business data
   * Use for: Most business entities that change occasionally
   * Examples: Entities, Users, Signatories, Bank Accounts
   */
  NORMAL: 5 * 60 * 1000,

  /**
   * SLOW (30 minutes) - Long caching for reference data
   * Use for: Data that rarely changes
   * Examples: Banks, Bank Branches, Settings, Configuration
   */
  SLOW: 30 * 60 * 1000,

  /**
   * STATIC (Infinity) - Never considered stale
   * Use for: Immutable reference data, constants, enums
   * Examples: Country codes, currency lists, transaction types definitions
   */
  STATIC: Infinity,
} as const;

/**
 * Type-safe staleTime value type
 */
export type StaleTimeValue = typeof STALE_TIME[keyof typeof STALE_TIME];

/**
 * Alias for backward compatibility and consistency across the codebase
 */
export const staleTimeConfig = STALE_TIME;
